import React, { type CSSProperties, type ComponentType, type ElementType, type ReactNode, useMemo } from 'react';
import cn from 'classnames';

import { _isNotBlank, _notNil } from '@/littledash';

import SortableItem from '@/components/Draggable/SortableItem';
import SortableList from '@/components/Draggable/SortableList';
import AddButton from '@/components/UI/AddButton';
import RemoveButton from '@/components/UI/RemoveButton';

interface SortableConfig {
  droppableId: string;
  onReorder: (sourceIndex: number, destinationIndex: number) => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

interface HtmlProps {
  className?: string;
  style?: CSSProperties;
}

interface ListItem {
  id: string;
}

interface ListProps<T extends ListItem> {
  data?: Array<T>;
  renderItem: (value: T, index: number, array: Array<T>) => ReactNode;
  removeButtonClasses?: string;
  canAmend?: boolean;
  canAdd?: boolean;
  canRemove?: boolean;
  onAddClick?: () => void;
  onRemoveClick?: (index: number) => void;
  addButtonText?: string;
  addButtonProps?: HtmlProps;
  isRemoveButtonFluid?: boolean;
  itemProps?: HtmlProps;
  itemsContainerProps?: HtmlProps;
  ItemsContainerType?: ElementType;
  containerProps?: HtmlProps;
  sortable?: SortableConfig;
}

const DraggableList = <T extends ListItem>({
  data = [],
  renderItem,
  canAmend,
  canAdd,
  canRemove,
  onAddClick,
  onRemoveClick,
  addButtonText = '',
  addButtonProps = {},
  isRemoveButtonFluid = true,
  itemProps = {},
  itemsContainerProps = {},
  ItemsContainerType = 'span',
  containerProps = {},
  removeButtonClasses = '',
  sortable,
}: ListProps<T>): React.JSX.Element => {
  const items = useMemo(() => data.map(renderItem), [data, renderItem]);
  const { className: itemsContainerClassName, ...restItemsContainerProps } = itemsContainerProps;

  const isSortable = _notNil(sortable);
  const childrenList = React.Children.map(items, (child, index) => {
    const { className: itemClassName, ...restItemProps } = itemProps;
    return (
      <div
        key={data[index].id}
        className={cn(
          'flex flex-row items-start',
          { 'justify-between': isRemoveButtonFluid, mb3: !isSortable },
          !isSortable ? itemClassName : undefined
        )}
        {...(!isSortable ? restItemProps : {})}
      >
        {child}
        {canAmend === true && (
          <RemoveButton
            className={cn('ml2', removeButtonClasses)}
            style={{ visibility: canRemove === true ? 'visible' : 'hidden' }}
            onClick={() => onRemoveClick?.(index)}
          />
        )}
      </div>
    );
  });

  const ItemsContainer = ItemsContainerType ?? 'span';

  const itemsContent = isSortable ? (
    <SortableList
      droppableId={sortable.droppableId}
      onReorder={sortable.onReorder}
      disabled={sortable.disabled}
      className={sortable.className}
      style={sortable.style}
    >
      {childrenList}
    </SortableList>
  ) : (
    childrenList
  );

  return (
    <div {...containerProps} data-test-component="List" data-test-element="container">
      <ItemsContainer className={itemsContainerClassName} {...restItemsContainerProps}>
        {itemsContent}
      </ItemsContainer>
      {canAdd === true && canAmend === true && (
        <AddButton onClick={onAddClick} className={addButtonProps.className}>
          {_isNotBlank(addButtonText) ? addButtonText : 'Add item'}
        </AddButton>
      )}
    </div>
  );
};

export default DraggableList;

export const asListItem =
  <P,>(Component: ComponentType<P & { index: number }>, props?: Partial<P>) =>
  (item: ListItem, index: number): ReactNode => {
    const merged = { ...props, ...item, index } as unknown as P & { index: number };
    return <Component {...merged} />;
  };

interface SortableItemConfig {
  canSort: boolean;
  itemClassName?: string;
}

export const asSortableListItem =
  (sortableConfig: SortableItemConfig) =>
  <P,>(Component: ComponentType<P & { index: number }>, props?: Partial<P>) =>
  (item: ListItem, index: number): ReactNode => {
    const merged = { ...props, ...item, index } as unknown as P & { index: number };
    const component = <Component {...merged} />;
    const rowClassName = cn(
      'flex flex-row justify-between items-center w-100 hide-child',
      sortableConfig.itemClassName
    );

    if (sortableConfig.canSort) {
      return (
        <SortableItem key={item.id} id={String(item.id)} index={index} showDraggableIcon className={rowClassName}>
          <div className="flex flex-row">{component}</div>
        </SortableItem>
      );
    }

    if (_isNotBlank(sortableConfig.itemClassName ?? '')) {
      return (
        <div key={item.id} className={rowClassName}>
          <div className="flex flex-row">{component}</div>
        </div>
      );
    }

    return component;
  };
